import { ChevronRight } from "lucide-react";
import { Skeleton } from "primereact/skeleton";
import { PropsWithChildren, ReactElement } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "src/components/ui/collapsible";
import { SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu } from "src/components/ui/sidebar";
import { useGetSurveyAudienceQuery } from "src/redux/survey/survey.endpoints";
import { useViewSurveyChildren, ViewSurveyChildrenProps } from "../useViewSurveyChildren";
import { DetailsLayout, DetailsTitle, ResultsSidebarDetailsAudience, ResultsSidebarDetailsRespondents } from "./results-sidebar-details-section";

const Layout = ({ children }: PropsWithChildren): ReactElement => {
  return (
    <Collapsible
      title="Survey Details"
      className="group/collapsible"
    >
      <SidebarGroup className="px-6 py-3">
        <SidebarGroupLabel
          asChild
          className="group/label h-12 text-base text-sidebar-foreground pt-3 px-0 border-t rounded-none"
        >
          <CollapsibleTrigger>
            Survey Details
            <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent>
          <SidebarGroupContent>
            <SidebarMenu>
              <div className="flex flex-col gap-6 py-4">
                {children}
              </div>
            </SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
};

export const SurveyResultsSidebarDetails = ({ surveyId, isShare }: ViewSurveyChildrenProps): ReactElement | null => {
  const { survey, stats, summary } = useViewSurveyChildren(surveyId, isShare);
  const { criteria } = useGetSurveyAudienceQuery({ id: survey?.audience ?? '', isShare }, {
    skip: !survey?.audience,
    selectFromResult: ({ data }) => ({ criteria: data?.criteria }),
  });

  if (!survey || !stats) {
    return (
      <Layout>
        <DetailsLayout>
          <DetailsTitle
            skeletonTitle="Respondents"
            variant="skeleton"
            skeletonCount
          />
          <Skeleton className="h-6" />
          <Skeleton className="h-6" />
        </DetailsLayout>
      </Layout>
    );
  }

  const { aiUsers, humanUsers, totalUsers } = stats;

  return (
    <Layout>
      <ResultsSidebarDetailsAudience criteria={criteria} />
      <ResultsSidebarDetailsRespondents
        aiUsers={aiUsers}
        humanUsers={humanUsers}
        totalUsers={totalUsers}
        alignmentScore={summary?.alignmentScore}
      />
    </Layout>
  );
};
